'use client';

import {
  getFacetedUniqueValues as clientGetFacetedUniqueValues,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowData,
  SortingState,
  TableMeta,
  Table as TableType,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { SquareMinus, SquarePlus } from 'lucide-react';
import * as React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useControl } from 'src/hooks';
import { cn } from 'src/lib/utils';

import { DataTablePagination } from './data-table-pagination';

const DefaultPageSize = 10;
const DefaultLargePageSize = 10000;

export type WithChildren<TData> = TData & { children?: WithChildren<TData>[] };

interface DataTableProps<TData, TValue = unknown> {
  columns: ColumnDef<WithChildren<TData>, TValue>[];
  data: WithChildren<TData>[];
  enableRowSelection?: boolean;
  toolbarComponent?: React.ComponentType<DataTableToolbarProps<TData>>;

  // Manual modes
  manualPagination?: boolean;
  manualFiltering?: boolean;
  manualSorting?: boolean;

  // Row count for manual pagination
  rowCount?: number;

  // Pagination
  pagination?: PaginationState;
  defaultPagination?: PaginationState;
  onPaginationChange?: (
    updater: PaginationState | ((old: PaginationState) => PaginationState)
  ) => void;

  // Column Filters
  columnFilters?: ColumnFiltersState;
  defaultColumnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (
    updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)
  ) => void;

  //Global Filter
  globalFilter?: string;
  onGlobalFilterChange?: (value: string | undefined) => void;

  // Sorting
  sorting?: SortingState;
  defaultSorting?: SortingState;
  onSortingChange?: (updater: SortingState | ((old: SortingState) => SortingState)) => void;

  // Column Visibility
  columnVisibility?: VisibilityState;
  defaultColumnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState)
  ) => void;

  // 统计
  getFacetedUniqueValues?: <TData extends RowData>() => (
    table: TableType<WithChildren<TData>>,
    columnId: string
  ) => () => Map<any, number>;

  hidePagination?: boolean;
  className?: string;
  meta?: TableMeta<WithChildren<TData>>;

  // 其他属性
  pending?: boolean;
  emptyDesc?: React.ReactNode;
  /** 如果为 true，初始时展开所有行（默认折叠） */
  defaultExpandAll?: boolean;
  /** 在第一列显示树形展开/折叠图标（需数据含 children） */
  showTreeToggle?: boolean;
}

// 定义 DataTableToolbarProps 接口
interface DataTableToolbarProps<TData> {
  table: TableType<WithChildren<TData>> | TableType<TData>;
}

export function DataTable<TData, TValue = unknown>({
  columns,
  data,
  enableRowSelection,
  toolbarComponent: DataTableToolbar,

  // Manual modes
  manualPagination = false,
  manualFiltering = false,
  manualSorting = false,

  // Row count for manual pagination
  rowCount,

  // Pagination
  pagination: controlledPagination,
  defaultPagination,
  onPaginationChange,

  // Column Filters
  columnFilters: controlledColumnFilters,
  defaultColumnFilters,
  onColumnFiltersChange,

  // Global Filter
  globalFilter: controlledGlobalFilter,
  onGlobalFilterChange,

  // Sorting
  sorting: controlledSorting,
  defaultSorting,
  onSortingChange,

  // Column Visibility
  columnVisibility: controlledColumnVisibility,
  defaultColumnVisibility,
  onColumnVisibilityChange,

  // 统计
  getFacetedUniqueValues = clientGetFacetedUniqueValues,

  hidePagination,
  className,
  meta,
  emptyDesc = '暂无数据',

  pending,
  defaultExpandAll = false,
  showTreeToggle = false,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [pendingState, setPendingState] = useControl(false, pending, undefined);

  // 使用 useControl 来处理受控和非受控状态
  const [pagination, setPagination] = useControl(
    defaultPagination ?? {
      pageIndex: 0,
      pageSize: hidePagination ? DefaultLargePageSize : DefaultPageSize,
    },
    controlledPagination,
    onPaginationChange
  );

  const [columnFilters, setColumnFilters] = useControl(
    defaultColumnFilters || [],
    controlledColumnFilters,
    onColumnFiltersChange
  );

  // Global filter 支持（受控/非受控）
  const [globalFilter, setGlobalFilter] = useControl<string | undefined>(
    undefined,
    controlledGlobalFilter,
    onGlobalFilterChange
  );

  const [sorting, setSorting] = useControl(
    defaultSorting || [],
    controlledSorting,
    onSortingChange
  );

  const [columnVisibility, setColumnVisibility] = useControl(
    defaultColumnVisibility || {},
    controlledColumnVisibility,
    onColumnVisibilityChange
  );

  const table = useReactTable({
    data,
    columns,
    meta: {
      pending: pendingState,
      setPending: setPendingState,
      ...meta,
    },
    rowCount,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
      sorting,
      pagination,
      globalFilter,
    },
    enableRowSelection,
    manualPagination,
    manualFiltering,
    manualSorting,
    getSubRows: (row) => row.children,
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // 如果需要默认展开所有行（例如用于测试或展示树形数据），在 data 变化时触发展开
  React.useEffect(() => {
    if (defaultExpandAll) {
      table.toggleAllRowsExpanded(true);
    }
  }, [data, defaultExpandAll, table]);

  return (
    <div className="space-y-4">
      {DataTableToolbar && <DataTableToolbar table={table} />}
      <div className={cn('rounded-md border relative', className)}>
        {pendingState && (
          <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              <span className="text-sm text-gray-600">加载中...</span>
            </div>
          </div>
        )}
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup, hgIndex) => (
              <TableRow key={`hg-${hgIndex}`}>
                {headerGroup.headers.map((header, hIndex) => {
                  return (
                    // 使用 header.id + 索引 来保证唯一性
                    <TableHead
                      key={`${header.id ?? 'header'}-${hIndex}`}
                      colSpan={header.colSpan}
                      className="px-3"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const cells = row.getVisibleCells();
                return (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                    {cells.map((cell, idx) => {
                      // 如果启用了树形切换，则在第一列渲染图标与缩进
                      if (idx === 0 && showTreeToggle) {
                        const canExpand = row.getCanExpand();
                        const isExpanded = row.getIsExpanded();
                        return (
                          <TableCell key={cell.id} className="px-3 py-1">
                            <div className="min-h-8 flex items-center">
                              <div className="flex items-center">
                                <div
                                  style={{
                                    width: 28,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginLeft: (row.depth ?? 0) * 6,
                                  }}
                                >
                                  {canExpand ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        (row as any).toggleExpanded?.();
                                      }}
                                      className="inline-flex items-center justify-center w-5 h-5 text-muted-foreground"
                                      aria-label={isExpanded ? '折叠' : '展开'}
                                    >
                                      {isExpanded ? (
                                        <SquareMinus className="w-4 h-4" />
                                      ) : (
                                        <SquarePlus className="w-4 h-4" />
                                      )}
                                    </button>
                                  ) : (
                                    <span className="w-5 h-5 inline-block" />
                                  )}
                                </div>

                                <div
                                  className={cn('ml-0', {
                                    'text-foreground': (row.depth ?? 0) === 0 && row.getCanExpand(),
                                    'text-muted-foreground': (row.depth ?? 0) > 0,
                                  })}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        );
                      }

                      const cellValue = cell.getValue();
                      const highlight =
                        (row.depth ?? 0) === 0 &&
                        row.getCanExpand() &&
                        typeof cellValue === 'number' &&
                        cellValue > 0;

                      const isChild = (row.depth ?? 0) > 0;

                      return (
                        <TableCell key={cell.id} className="px-3 py-1">
                          <div
                            className={cn('min-h-8 flex items-center', {
                              'text-foreground': highlight,
                              'text-muted-foreground': isChild,
                            })}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {emptyDesc}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!hidePagination && <DataTablePagination table={table} />}
    </div>
  );
}
